import { TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { Tabs } from '../ui/tabs';
import { capitalize } from 'src/utils';
import { MAIN_BG_COLOR, MAIN_TXT_COLOR } from './common';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';

interface Tab {
    label: string,
    component?: JSX.Element,
    icon?: JSX.Element;
    disabled: boolean,
    visible?: boolean
}

interface Props {
    tabs: Tab[],
    refreshContent?: boolean,
    paramsUse?: boolean,
    backLabel?: {
        label: string,
        url: string
    },
    home?: boolean,
    tabsPosition?: 'top' | 'left';
    heightContainerR?: string;
    colorPrimary?: string;
    tabsOptionsStyle?: string;
    getActiveTab?: (value: string) => void;
}

export const TabsComponent = ({ 
    tabs, refreshContent = true, 
    paramsUse = false, 
    backLabel, 
    home = false, 
    tabsPosition = 'top', 
    heightContainerR, 
    colorPrimary, 
    tabsOptionsStyle,
    getActiveTab 
}: Props) => {
    const tabsVisible = tabs.filter(tab => tab.visible || tab.visible === undefined);
    const positionLeft = tabsPosition === 'left';
    const [params, setParams] = useSearchParams();
    const tabSelected = params.get('mode') || tabsVisible[0].label.toLowerCase().replaceAll(' ', '_');
    const [tabSelected2, setTabSelected2] = useState(tabsVisible[0].label.toLowerCase().replaceAll(' ', '_'));
    const navigate = useNavigate();

    const classNameTrigger = (tab: Tab) => {
        if(positionLeft) {
            return `
                rounded py-2 px-4 
                disabled:cursor-not-allowed
                flex gap-2 items-center
                hover:bg-gray-100
                ${
                    paramsUse ?
                        tabSelected === tab.label.toLowerCase().replaceAll(' ', '_') ? 'text-primary'  : ' '
                        : tabSelected2 === tab.label.toLowerCase().replaceAll(' ', '_') ? 'text-primary' : ' '
                }
                ${tabsOptionsStyle ? tabsOptionsStyle : ''}
            `;
        } else {
            return `
                rounded-t py-2 px-4 
                disabled:cursor-not-allowed
                flex gap-2 items-center
                ${
                    paramsUse ?
                        tabSelected === tab.label.toLowerCase().replaceAll(' ', '_') ? MAIN_BG_COLOR + ' ' + MAIN_TXT_COLOR : ' '
                        : tabSelected2 === tab.label.toLowerCase().replaceAll(' ', '_') ? MAIN_BG_COLOR + ' ' + MAIN_TXT_COLOR : ' '
                }
                ${tabsOptionsStyle ? tabsOptionsStyle : ''}
            `;
        }
    }
    

    function changeValue(value: string) {
        if (paramsUse) {
            params.set('mode', value);
            setParams(params);
        } else {
            setTabSelected2(value);
            getActiveTab?.(value);
        }
    }

    return (
        <Tabs 
            defaultValue={paramsUse ? tabSelected : tabSelected2} 
            className={`${positionLeft ? 'flex gap-4' : ''}`}
            onValueChange={(e) => changeValue(e)}
        >
            <TabsList className={`${positionLeft ? 'w-[25%] flex-col' : 'justify-between gap-3 mx-auto'} flex`}>
                {backLabel && (
                    <div className={`flex gap-2 items-center ${positionLeft ? 'mb-4' : ''}`}>
                        <ArrowLeft className="cursor-pointer" onClick={() => {
                            if(home) {
                                window.location.href = window.location.origin;
                            } else {
                                navigate(backLabel.url);
                            }
                        }} />
                        <p>{backLabel.label}</p>
                    </div>
                )}
                <div className={`${positionLeft ? 'flex-col border border-gray-200 rounded' : ''} flex`}>
                {
                    tabsVisible.map(tab => (
                        <TabsTrigger 
                        key={tab.label} 
                        value={tab.label.toLowerCase().replaceAll(' ', '_')}   
                        className={classNameTrigger(tab)} 
                        disabled={tab.disabled}
                        style={colorPrimary ? 
                            {backgroundColor: paramsUse ?
                                tabSelected === tab.label.toLowerCase().replaceAll(' ', '_') ? colorPrimary : ''
                                : tabSelected2 === tab.label.toLowerCase().replaceAll(' ', '_') ? colorPrimary : ''
                            } : {}}
                        >
                            {
                                tab.icon && (
                                    <div className='flex justify-center w-7'>
                                        {tab.icon}
                                    </div>
                                )
                            }            
                            {capitalize(tab.label).replace('_', ' ')}
                        </TabsTrigger>
                    ))
                }
                </div>
            </TabsList>
            
            {
                tabsPosition === 'top' && <div className={`h-[1px] ${MAIN_BG_COLOR}`} style={colorPrimary ? 
                    {backgroundColor: colorPrimary} : {}} />
            }
            <div 
                className={`${positionLeft ? 'w-[75%] overflow-auto h-[calc(100vh_-_210px)] px-2' : ''}`}
                style={heightContainerR ? { height: heightContainerR } : {}}
            >
                <div className={`${positionLeft ? 'w-full border border-gray-200 rounded' : ''}`}>
                {
                    tabsVisible.map((tab) => refreshContent ? (
                        <TabsContent 
                            key={tab.label} 
                            value={tab.label.toLowerCase().replaceAll(' ', '_')}
                        >
                            {tab.component}
                        </TabsContent>
                    ) : (
                        <TabsContent
                            key={tab.label} 
                            value={tab.label.toLowerCase().replaceAll(' ', '_')} 
                            forceMount
                        >
                            <div aria-selected={paramsUse ? tabSelected === tab.label.toLowerCase().replaceAll(' ', '_') : tabSelected2 === tab.label.toLowerCase().replaceAll(' ', '_')}
                                className="aria-selected:block hidden">
                                {tab.component}
                            </div>
                        </TabsContent>
                    ))
                }
                </div>
            </div>
        </Tabs>
    );
};
